import { useEffect, useReducer, useCallback, useMemo } from 'react'
import { refresh } from  '../../../out-game/utils/request-factory'

import {
  SET_USERS_LIST,
  SET_MESSAGES_LIST,
  ADD_MESSAGE,
  SET_ERROR_MESSAGE,
  SET_USER_BLOCKED,
  SET_MESSAGE_DELETED,
  SET_IS_SOCKET_OPENED,
} from '../../util/actionTypes'

import { initialState, socketReducer } from './socketReducer'

import {
  CommandDataType,
} from './useSocket.types'

import { sortMessagesByDate } from '../../util/util'
import { getItem } from '../../agent/localStore'

type SocketError = Event & { target: {url: string }}

const useSocket = (
  url: string,
  username: string | null,
  setWsGeneralChat: (value: WebSocket | null) => void,
  wsGeneralChat: WebSocket | null,
  setIsNotificationsEnabled: (value: boolean) => void,
  setIsUserConnected: (value: boolean) => void,
  isUserConnected: boolean,
  setUnreadMessages: (value: boolean) => void,
  isChatOpen: boolean,
  isNotificationsEnabled?: boolean,
) => {
  const isUserExist = !!username
  const token = getItem('access_token')

  const address = isUserExist ? `${url}/?access_token=${token}` : `${url}/`
  const [stateReducer, dispatchSocket] = useReducer(socketReducer, initialState)
  const onErrorSocket = (e: Event) => {
    const newToken = getItem('access_token')
    if ((e as SocketError)?.target?.url === `${url}/?access_token=${newToken}`) {
      refresh()
    }
  }
  const audio = useMemo(() => {
    const newAudio = new Audio()
    newAudio.src = '/bell.mp3'
    return newAudio
  }, [])

  const sendCommand = (command: CommandDataType) => {
    if (wsGeneralChat && wsGeneralChat.readyState > 0) {
      wsGeneralChat.send(JSON.stringify(command))
    }
  }

  const setErrorMessage = useCallback(
    (error: string) => {
      dispatchSocket({ type: SET_ERROR_MESSAGE, payload: error })
    },
    [dispatchSocket]
  )

  const setUserBlocked = useCallback(
    (value: boolean) => {
      dispatchSocket({ type: SET_USER_BLOCKED, payload: value })
    },
    [dispatchSocket]
  )

  const onExit = useCallback(() => {
    //выход пользователя
    if (wsGeneralChat) {
      wsGeneralChat.close()
    }
  }, [wsGeneralChat])

  const onOpenSocket = useCallback((e: Event) => {
    dispatchSocket({ type: SET_IS_SOCKET_OPENED, payload: true })
  }, [])

  const onMessageSocket = useCallback(
    (e: MessageEvent) => {
      const data = JSON.parse(e.data)
      switch (data.event) {
        case 'chat_info':
          setIsNotificationsEnabled(data.data.chat_settings.is_notifications_enabled)
          break
        case 'chat_settings':
          setIsNotificationsEnabled(data.data.chat_settings.is_notifications_enabled)
          break
        case 'users_list':
          dispatchSocket({ type: SET_USERS_LIST, payload: data.data.users })
          break
        case 'message_sent':
          dispatchSocket({ type: ADD_MESSAGE, payload: data.data.message })
          if (
            isNotificationsEnabled &&
            isUserConnected &&
            username !== data.data.message.author
          ) {
            audio.play()
          }
          if (
            !isUserConnected &&
            username !== data.data.message.author
          ) {
            setUnreadMessages(true)
          }
          break
        case 'last_messages':
          dispatchSocket({
            type: SET_MESSAGES_LIST,
            payload: sortMessagesByDate(data.messages),
          })
          break
        case 'error_message':
          return setErrorMessage(data.error)
        case 'user_blocked':
          return setUserBlocked(true)
        case 'message_deleted':
          dispatchSocket({ type: SET_MESSAGE_DELETED, payload: data.data.deleted_message })
          break
        case 'user_joined_the_chat':
          dispatchSocket({ type: SET_USERS_LIST, payload: data.data.users_list.users })
          break
        case 'user_left_the_chat':
          dispatchSocket({ type: SET_USERS_LIST, payload: data.data.users_list.users })
          break
        case 'user_deleted':
          if (wsGeneralChat && wsGeneralChat.readyState > 0) {
            chatActions.sendCommand({ action: 'get_last_messages' })
          }
          break
      }
    },
    [
      isNotificationsEnabled,
      isUserConnected,
      audio,
      dispatchSocket,
      setErrorMessage,
      setUserBlocked,
    ]
  )

  const onCloseSocket = useCallback(
    (e: Event) => {
      setWsGeneralChat(null)
    },
    [setWsGeneralChat]
  )

  useEffect(() => {
    if (
      wsGeneralChat &&
      wsGeneralChat.readyState > 0 &&
      (!stateReducer.usersList || !stateReducer.usersList.length) &&
      !isUserExist
    ) {
      chatActions.sendCommand({ action: 'get_online_users' })
    }
    if (
      wsGeneralChat &&
      wsGeneralChat.readyState > 0 &&
      (!stateReducer.usersList || !stateReducer.usersList.length) &&
      isUserExist
    ) {
      chatActions.sendCommand({ action: 'get_user_list' })
    }
    if (
      wsGeneralChat &&
      wsGeneralChat.readyState > 0 &&
      (!stateReducer.messagesList || !stateReducer.messagesList.length)
    ) {
      chatActions.sendCommand({ action: 'get_last_messages' })
    }
  }, [wsGeneralChat, stateReducer.usersList, stateReducer.messagesList])

  useEffect(() => {
    if (
      wsGeneralChat &&
      wsGeneralChat.url !== address &&
      wsGeneralChat.readyState > 0
    ) {
      wsGeneralChat.close()
    }

    if (!isUserExist && !isChatOpen) { return }

    if (
      !wsGeneralChat ||
      (wsGeneralChat &&
        wsGeneralChat.url !== address &&
        wsGeneralChat.readyState > 1)
    ) {
      const websocket = new WebSocket(address)
      websocket.onopen = onOpenSocket
      websocket.onmessage = onMessageSocket
      websocket.onclose = onCloseSocket
      websocket.onerror = onErrorSocket
      setWsGeneralChat(websocket)
    }

    if (wsGeneralChat) {
      wsGeneralChat.onmessage = onMessageSocket
    }

    return () => {
      if (wsGeneralChat) {
        // TODO wsGeneralChat.close();
      }
    }
  }, [
    address,
    wsGeneralChat,
    onOpenSocket,
    onMessageSocket,
    onCloseSocket,
    setWsGeneralChat,
    isNotificationsEnabled,
    isUserConnected,
    isUserExist,
    isChatOpen
  ])

  const chatState = {
    usersList: stateReducer.usersList,
    messagesList: stateReducer.messagesList,
    error: stateReducer.error,
    isUserBlocked: stateReducer.isUserBlocked,
  }

  const chatActions = {
    sendCommand,
    setErrorMessage,
    setUserBlocked,
  }

  return {
    chatState,
    chatActions,
  }
}

export default useSocket
